"use client";

import { useState } from "react";
import Link from "@/components/i18n/link";
import Image from "next/image";
import { MapPin, Calendar } from "lucide-react";
import { isOptimizableImageHost, type CiudadVivaEvent } from "@/lib/events";
import { trackCardClick } from "@/lib/analytics";
import { InterestButton } from "./InterestButton";
import { useLocale, useLocalizedPathname, useMessages } from "@/lib/i18n/client";
import { formatMoney } from "@/lib/format";
import { track } from "@/lib/tracking/track";
import type { EventList } from "@/lib/tracking/events";
import eventMessages from "@/lib/events.i18n";
import messages from "./EventCard.i18n";

// Desde qué lista se abrió el evento, según la página donde está la card.
function listFromPath(path: string): EventList {
  if (path === "/") return "home";
  if (path === "/eventos") return "events";
  if (path.startsWith("/eventos/categoria/")) return "category";
  if (path.startsWith("/eventos/comuna/")) return "district";
  if (path.startsWith("/evento/")) return "related";
  if (path === "/interes") return "saved";
  if (path.startsWith("/organizaciones/")) return "organization";
  if (path.startsWith("/viva-cine/")) return "viva_cine";
  if (path.startsWith("/juegos/sopa-de-letras")) return "word_search";
  if (path.startsWith("/juegos/palabra-del-dia")) return "daily_word";
  return "other";
}

interface Props {
  event: CiudadVivaEvent;
  size?: "default" | "wide";
  // Posición en la lista (desde 1), para analítica.
  position?: number;
}

export function EventCard({ event, size = "default", position }: Props) {
  const isFree = event.price === "free";
  const isCanceled = event.status === "canceled";
  const [imgSrc, setImgSrc] = useState(event.image);
  const unoptimized = !isOptimizableImageHost(imgSrc);
  const t = useMessages(messages);
  const lang = useLocale();
  const tEvents = useMessages(eventMessages);
  const pathname = useLocalizedPathname();

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl bg-card shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
      {/* Capa de click de toda la card. El InterestButton queda FUERA de este
          <a> a propósito (hermano, no hijo) — NavigationOverlay intercepta
          clicks en fase de capture buscando el <a> más cercano, así que un
          botón anidado dentro del Link dispara el loader de navegación aunque
          se llame preventDefault/stopPropagation en el handler del botón
          (la fase de capture del documento ya corrió antes de llegar al
          botón). Sacarlo del árbol del <a> es lo único que evita ese trigger. */}
      <Link
        href={`/evento/${event.slug}`}
        data-track-impression={event.slug}
        onClick={() => {
          trackCardClick(event.slug);
          track("event_card_click", { slug: event.slug, list: listFromPath(pathname), position });
        }}
        aria-label={event.title}
        className="absolute inset-0 z-10 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className={`relative overflow-hidden bg-muted ${size === "wide" ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        <Image
          src={imgSrc}
          alt=""
          aria-hidden
          fill
          unoptimized={unoptimized}
          onError={() => setImgSrc("/images/event-music.jpg")}
          className={`scale-110 object-cover blur-2xl brightness-75 ${isCanceled ? "opacity-60 grayscale" : ""}`}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <Image
          src={imgSrc}
          alt={event.title}
          fill
          unoptimized={unoptimized}
          onError={() => setImgSrc("/images/event-music.jpg")}
          className={`object-scale-down transition-transform duration-700 group-hover:scale-105 ${isCanceled ? "opacity-60 grayscale" : ""}`}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <div className="absolute left-3 top-3 flex gap-2">
          {isCanceled ? (
            <span className="rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-red-600 dark:bg-red-950/60 dark:text-red-400">
              {t.canceled}
            </span>
          ) : (
            <>
              {event.today && (
                <span className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground">
                  {t.today}
                </span>
              )}
              {isFree && (
                <span className="rounded-full bg-foreground px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-background">
                  {t.free}
                </span>
              )}
              {event.sponsored && (
                <span className="rounded-full bg-background/95 px-2.5 py-1 text-[11px] font-medium text-muted-foreground backdrop-blur">
                  {t.sponsored}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="absolute right-3 top-3 z-20">
        <InterestButton event={event} variant="compact" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {tEvents.categories[event.category] ?? event.category}
        </p>
        <h3 className="text-lg font-semibold leading-snug text-foreground line-clamp-2">
          {event.title}
        </h3>
        <div className="mt-auto flex flex-col gap-1.5 pt-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> {event.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />{" "}
            {event.isOnline
              ? t.online
              : [event.district, event.region !== event.district ? event.region : undefined].filter(Boolean).join(", ")}
          </span>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm font-semibold text-foreground">
            {event.price === "free"
              ? t.free
              : event.price === "consultar"
              ? t.seePrice
              : t.from(formatMoney(event.price as number, event.country, lang))}
          </span>
          <span className="text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
            {t.details}
          </span>
        </div>
      </div>
    </div>
  );
}
